import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Card, Title, Paragraph, Button, useTheme, ActivityIndicator, Portal, Snackbar } from 'react-native-paper';
import { supabase } from '../../services/supabase';
import Icon from 'react-native-vector-icons/MaterialIcons';

type Props = {
  navigation: any;
};

type MenuItem = {
  title: string;
  description: string;
  icon: string;
  route: string;
};

export function AdministracaoHome({ navigation }: Props) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const menuItems: MenuItem[] = [
    {
      title: 'Configuração da Evolution API',
      description: 'Configure a integração com o WhatsApp',
      icon: 'api',
      route: 'ConfiguracaoEvolutionAPI'
    },
    {
      title: 'Configuração Instâncias do WhatsApp',
      description: 'Configure instâncias do WhatsApp',
      icon: 'settings',
      route: 'GerenciarInfraestruturaChatbot'
    },
    {
      title: 'Gerenciar Empresas',
      description: 'Gerencie as empresas cadastradas',
      icon: 'domain',
      route: 'GerenciarEmpresas'
    },
    {
      title: 'Gerenciar Usuários',
      description: 'Gerencie os usuários do sistema',
      icon: 'people',
      route: 'GerenciarUsuarios'
    }
  ];

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Usuário não autenticado');
        return;
      }

      const { data, error } = await supabase
        .from('usuarios')
        .select('admin')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      if (!data?.admin) {
        setError('Acesso negado. Você não tem permissão de administrador.');
        return;
      }

      setIsAdmin(true);
    } catch (err: any) {
      setError(err.message || 'Erro ao verificar permissões');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (!isAdmin) {
    return (
      <View style={styles.container}>
        <Card style={styles.errorCard}>
          <Card.Content>
            <Title>Acesso Restrito</Title>
            <Paragraph>Esta área é restrita a administradores do sistema.</Paragraph>
          </Card.Content>
        </Card>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView>
        <Title style={styles.title}>Administração do Sistema</Title>
        <Paragraph style={styles.subtitle}>Gerencie configurações, usuários e empresas</Paragraph>

        <View style={styles.cardContainer}>
          {menuItems.map((menuItem, index) => (
            <Card key={index} style={styles.card} onPress={() => navigation.navigate(menuItem.route)}>
              <Card.Content>
                <Icon name={menuItem.icon} size={40} color={theme.colors.primary} style={styles.cardIcon} />
                <Title>{menuItem.title}</Title>
                <Paragraph>{menuItem.description}</Paragraph>
              </Card.Content>
            </Card>
          ))}
        </View>
      </ScrollView>

      <Portal>
        <Snackbar
          visible={!!error}
          onDismiss={() => setError(null)}
          duration={3000}
          style={{ backgroundColor: '#F44336' }}
        >
          {error}
        </Snackbar>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    color: '#666',
  },
  cardContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  card: {
    width: '48%',
    marginBottom: 16,
    elevation: 4,
  },
  cardIcon: {
    alignSelf: 'center',
    marginBottom: 8,
  },
  errorCard: {
    marginTop: 20,
    backgroundColor: '#ffebee',
  },
});
